import React, { useState, useEffect } from "react";
import styled, { css, keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { FiHome, FiShoppingCart, FiTruck } from "react-icons/fi";
import MainContainer from "../components/MainContainer";
import ProductSlider from "../components/utils/ProductSlider";
import { TbMoneybag } from "react-icons/tb";
import ImageModal from "../components/utils/ImageModal";
import { priceFormatter } from "../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import {
  selectProduct,
  selectProductData,
} from "../store/slices/products/slice";
import { useParams } from "react-router-dom";
import { FRONT_URL } from "../utils/constants";
import { getProduct } from "../store/slices/products/asyncThunks";
import SkeletonProductPage from "../components/utils/SkeletonProductPage";
import SkeletonProductCard from "../components/utils/SkeletonProductCard";
import { addToCart } from "../store/slices/cart/slice";
import { flyToCartFromImage } from "../utils/flyToCart";
import CustomToast from "../components/utils/CustomToast";
import ImagePlaceholder from "../components/utils/ImagePlaceholder";

const ProductPage = () => {
  const product = useSelector(selectProductData);
  const { loading } = useSelector(selectProduct);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageThumbnailLoaded, setImageThumbnailLoaded] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    dispatch(getProduct(+id))
      .unwrap()
      .then((res) => {
        setActiveImage(res?.data?.photos?.[0]?.photoName ?? null);
      })
      .catch((err) => console.log(err.message));
  }, [dispatch, id]);

  useEffect(() => {
    if (product?.name) {
      document.title = `${product.name} | OBSESSED`;
    }
  }, [product?.name]);

  const incrementQty = () => setQuantity((prev) => prev + 1);
  const decrementQty = () => setQuantity((prev) => (prev > 1 ? prev - 1 : 1));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  useEffect(() => {
    if (modalOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [modalOpen]);

  return (
    <MainContainer>
      {loading ? (
        <PageWrapper style={{ paddingBottom: 0, marginBottom: 0 }}>
          <SkeletonProductPage />
        </PageWrapper>
      ) : (
        <PageWrapper style={{ paddingBottom: 0, marginBottom: 0 }}>
          <ImageSection>
            {product?.photos?.length > 0 && !imageLoaded && (
              <LoaderOverlay style={{ borderRadius: "1.2rem" }}>
                <l-line-spinner
                  size="16"
                  stroke="2"
                  speed="1"
                  color="#fe5f55"
                />
              </LoaderOverlay>
            )}
            {product?.photos?.length > 0 ? (
              <MainImage
                id="product-main-image"
                key={activeImage}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 0.72, 0.18, 1] }}
                src={`${FRONT_URL}/images/${activeImage}`}
                alt={product.name}
                onClick={() => {
                  const index = product.photos.findIndex(
                    (img) => img.photoName === activeImage,
                  );

                  setModalIndex(index);
                  setModalOpen(true);
                }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoaded(true)}
              />
            ) : (
              <ImagePlaceholder
                iconSize={64}
                style={{
                  width: "50rem",
                  height: "50rem",
                  maxWidth: "100%",
                  borderRadius: "1.2rem",
                }}
              />
            )}
            <Thumbnails>
              {product.photos?.map((img) => (
                <div style={{ position: "relative" }}>
                  {!imageThumbnailLoaded && (
                    <LoaderOverlay>
                      <l-line-spinner
                        size="16"
                        stroke="2"
                        speed="1"
                        color="#fe5f55"
                      />
                    </LoaderOverlay>
                  )}
                  <Thumb
                    key={img.id}
                    src={`${FRONT_URL}/images/${img.photoName}`}
                    $active={img.photoName === activeImage}
                    onClick={() => setActiveImage(img.photoName)}
                    onLoad={() => setImageThumbnailLoaded(true)}
                    onError={() => setImageThumbnailLoaded(true)}
                  />
                </div>
              ))}
            </Thumbnails>
          </ImageSection>

          <InfoSection>
            <Kicker>
              {product?.categories?.[0]?.name || "Obsessed"} <span>改善</span>
            </Kicker>
            {product.badge && <Badge>{product.badge}</Badge>}
            <Title>{product.name}</Title>
            <Description>{product.description}</Description>

            <PriceBlock>
              <Stock $available={product.isInStock}>
                {product.comingSoon
                  ? "Uskoro"
                  : product.isInStock
                    ? "Na stanju"
                    : "Rasprodato"}
              </Stock>
              {product?.comingSoon ? (
                <Price>Uskoro u prodaji</Price>
              ) : product?.salePrice ? (
                <PriceRow>
                  <PriceOld>{priceFormatter(product.price)} RSD</PriceOld>
                  <Price>{priceFormatter(product.salePrice)} RSD</Price>
                </PriceRow>
              ) : (
                <Price>{priceFormatter(product.price)} RSD</Price>
              )}
            </PriceBlock>
            <OptionsRow>
              {product.isInStock && (
                <div>
                  <Label>Veličina:</Label>
                  <Sizes $shake={shake}>
                  {product.sizeS && (
                    <Size
                      $active={selectedSize === "S"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("S");
                      }}
                    >
                      S
                    </Size>
                  )}
                  {product.sizeM && (
                    <Size
                      $active={selectedSize === "M"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("M");
                      }}
                    >
                      M
                    </Size>
                  )}
                  {product.sizeL && (
                    <Size
                      $active={selectedSize === "L"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("L");
                      }}
                    >
                      L
                    </Size>
                  )}
                  {product.sizeXL && (
                    <Size
                      $active={selectedSize === "XL"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("XL");
                      }}
                    >
                      XL
                    </Size>
                  )}
                  {product.sizeSM && (
                    <Size
                      $active={selectedSize === "S/M"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("S/M");
                      }}
                    >
                      S/M
                    </Size>
                  )}
                  {product.sizeLXL && (
                    <Size
                      $active={selectedSize === "L/XL"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("L/XL");
                      }}
                    >
                      L/XL
                    </Size>
                  )}
                  {product.socksS && (
                    <Size
                      $active={selectedSize === "39-42"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("39-42");
                      }}
                    >
                      39-42
                    </Size>
                  )}
                  {product.socksM && (
                    <Size
                      $active={selectedSize === "43-46"}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedSize("43-46");
                      }}
                    >
                      43-46
                    </Size>
                  )}
                  </Sizes>
                </div>
              )}

              <div>
                <Label>Boja:</Label>
                <ColorDot $color={product.color} />
              </div>
            </OptionsRow>

            {!product.comingSoon && (
              <CtaRow>
                {product.isInStock && (
                  <QuantityRow>
                    <QuantityControl onClick={decrementQty}>–</QuantityControl>
                    <QuantityValue>{quantity}</QuantityValue>
                    <QuantityControl onClick={incrementQty}>+</QuantityControl>
                  </QuantityRow>
                )}
                <AddToCart
                $disabled={!product.isInStock || !selectedSize}
                disabled={!product.isInStock}
                onClick={() => {
                  if (!selectedSize) {
                    setShake(true);
                    setTimeout(() => setShake(false), 500);
                    return;
                  }
                  flyToCartFromImage(
                    document.getElementById("product-main-image")
                  );
                  dispatch(
                    addToCart({
                      ...product,
                      quantity,
                      selectedSize,
                    }),
                  );
                  setSelectedSize(null);
                  setQuantity(1);
                  setShowToast(true);
                }}
              >
                <FiShoppingCart size={20} />
                  {product.isInStock ? "Dodaj u korpu" : "Rasprodato"}
                </AddToCart>
              </CtaRow>
            )}

            <DeliveryFeatures>
              <Feature>
                <IconWrapper>
                  <FiHome size={24} />
                </IconWrapper>
                <Text>Dostava na kućnu adresu</Text>
              </Feature>

              <Feature>
                <IconWrapper>
                  <TbMoneybag size={24} />
                </IconWrapper>
                <Text>Plaćanje pouzećem</Text>
              </Feature>

              <Feature>
                <IconWrapper>
                  <FiTruck size={24} />
                </IconWrapper>
                <Text>Dostava kurirskom službom</Text>
              </Feature>
            </DeliveryFeatures>
          </InfoSection>
        </PageWrapper>
      )}
      <PageWrapper style={{ paddingTop: 0, marginTop: 0, width: "100%" }}>
        <SuggestedSection>
          <h2>Predloženi proizvodi</h2>
          {!loading && product?.categories?.[0]?.name ? (
            <ProductSlider
              category={product.categories[0].name}
              excludeId={+id}
            />
          ) : (
            <SuggestedList>
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonProductCard key={i} />
              ))}
            </SuggestedList>
          )}
        </SuggestedSection>
      </PageWrapper>
      <AnimatePresence>
        {modalOpen && product?.photos?.length > 0 && (
          <ImageModal
            images={product.photos}
            currentIndex={modalIndex}
            onClose={() => setModalOpen(false)}
            onNext={() =>
              setModalIndex((prev) => (prev + 1) % product.photos.length)
            }
            onPrev={() =>
              setModalIndex(
                (prev) =>
                  (prev - 1 + product.photos.length) % product.photos.length,
              )
            }
          />
        )}
      </AnimatePresence>

      {showToast && (
        <CustomToast
          message="Proizvod je dodat u korpu!"
          onClose={() => setShowToast(false)}
        />
      )}
    </MainContainer>
  );
};

export default ProductPage;

const DeliveryFeatures = styled.div`
  margin-top: 3rem;
  display: flex;
  flex-direction: row;
  max-width: 44rem;
  border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding-top: 2.2rem;
`;

const Feature = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
  padding: 0 1.6rem;

  &:first-child {
    padding-left: 0;
  }

  & + & {
    border-left: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  }
`;

const IconWrapper = styled.div`
  display: flex;
  color: var(--red, #fe5f55);

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }
`;

const Text = styled.div`
  font-size: 1.2rem;
  line-height: 1.5;
  font-family: "Poppins-Medium";
  color: var(--muted, #8e8c94);
`;

const PageWrapper = styled.div`
  max-width: 116rem;
  width: 100%;
  margin: 2rem auto 0;
  padding: 0 clamp(1.6rem, 4vw, 4.8rem);
  display: flex;
  justify-content: center;
  gap: clamp(2.4rem, 4vw, 5.6rem);
  flex-wrap: wrap;
  position: relative;
  color: var(--text, #f4f2ee);
`;

const ImageSection = styled.div`
  flex: 0 1 44rem;
  max-width: 44rem;
  height: fit-content;
  position: relative;

  @media (max-width: 900px) {
    flex: 1 1 100%;
    max-width: 52rem;
  }
`;

const MainImage = styled(motion.img)`
  width: 100%;
  aspect-ratio: 4 / 5;
  object-fit: cover;
  border-radius: 0.6rem;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  cursor: zoom-in;
  display: block;
`;

const Thumbnails = styled.div`
  display: flex;
  position: relative;
  gap: 1rem;
  margin-top: 1.2rem;
  flex-wrap: wrap;
`;

const Thumb = styled.img`
  width: 6.6rem;
  height: 7.8rem;
  object-fit: cover;
  border-radius: 0.4rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--line2, rgba(255,255,255,0.14))"};
  opacity: ${({ $active }) => ($active ? 1 : 0.55)};
  transition: all 0.25s var(--ease, ease);
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  min-width: 40rem;
  max-width: 52rem;
  @media (max-width: 420px) {
    min-width: 100%;
  }
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(2.8rem, 4.2vw, 4.4rem);
  line-height: 1.02;
  letter-spacing: 0.01em;
  max-width: 46rem;
  margin-bottom: 1.4rem;
  color: var(--text, #f4f2ee);
`;

const Description = styled.p`
  font-size: 1.4rem;
  font-family: "Poppins-Regular";
  color: var(--muted, #8e8c94);
  line-height: 1.7;
  margin-bottom: 2.4rem;
  max-width: 44rem;
`;

const Price = styled.div`
  font-size: 3.2rem;
  color: var(--red, #fe5f55);
  font-family: "Anton", sans-serif;
  font-weight: 400;
  letter-spacing: 0.02em;
`;

const Kicker = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--red, #fe5f55);
  font-family: "Poppins-Medium";
  margin-bottom: 1.4rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }

  span {
    font-family: var(--font-kanji);
    letter-spacing: 0.1em;
  }
`;

const PriceBlock = styled.div`
  border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-bottom: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 1.8rem 0;
  margin-bottom: 2.4rem;
  max-width: 44rem;
`;

const CtaRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
  max-width: 44rem;
  margin-top: 2.8rem;
`;

const shakeAnim = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-0.6rem); }
  40% { transform: translateX(0.6rem); }
  60% { transform: translateX(-0.4rem); }
  80% { transform: translateX(0.4rem); }
`;

const PriceOld = styled.span`
  font-size: 1.6rem;
  font-family: "Poppins-Regular";
  color: var(--muted2, #5c5a63);
  text-decoration: line-through;
`;

const PriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 1rem;
`;

const Stock = styled.div`
  font-size: 1.1rem;
  font-family: "Poppins-Medium";
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 0.6rem;
  color: ${({ $available }) =>
    $available ? "#7dd490" : "var(--red, #fe5f55)"};
`;

const OptionsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 3.2rem;

  & > div + div {
    border-left: 1px solid var(--line, rgba(255, 255, 255, 0.07));
    padding-left: 3.2rem;
  }
`;

const Sizes = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;

  ${(props) =>
    props.$shake &&
    css`
      animation: ${shakeAnim} 0.45s ease;

      & > div {
        border-color: var(--red, #fe5f55);
      }
    `}
`;

const Size = styled.div`
  padding: 0.8rem 1.4rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--line2, rgba(255,255,255,0.14))"};
  color: ${({ $active }) =>
    $active ? "var(--bg0, #0a0a0c)" : "var(--muted, #8e8c94)"};
  background: ${({ $active }) =>
    $active ? "var(--red, #fe5f55)" : "transparent"};
  border-radius: 2px;
  cursor: pointer;
  font-size: 1.3rem;
  letter-spacing: 0.06em;
  transition: 0.2s;
  font-family: ${({ $active }) =>
    $active ? '"Poppins-SemiBold"' : '"Poppins-Regular"'};
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  &:hover {
    border-color: ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--text, #f4f2ee)"};
    color: ${({ $active }) =>
      $active ? "var(--bg0, #0a0a0c)" : "var(--text, #f4f2ee)"};
  }
`;

const ColorDot = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 2px solid rgba(255, 255, 255, 0.85);
  margin-top: 0.6rem;
`;

const Badge = styled.div`
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  display: inline-block;
  padding: 0.5rem 1.2rem;
  border-radius: 2px;
  font-size: 1rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-family: "Poppins-Bold";
  margin-bottom: 1.4rem;
`;

const AddToCart = styled.button`
  flex: 1;
  justify-content: center;
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  padding: 1.5rem 3.2rem;
  font-size: 1.3rem;
  border-radius: 0.4rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: ${(props) =>
    props.$disabled
      ? "none"
      : "0 0.8rem 2.4rem -0.8rem rgba(254, 95, 85, 0.6)"};
  transition: all 0.3s var(--ease, ease);

  svg {
    width: 1.6rem;
    height: 1.6rem;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};
    transform: ${(props) => (props.$disabled ? "none" : "translateY(-2px)")};

    svg {
      transform: ${(props) =>
        props.$disabled ? "none" : "translateY(-0.2rem) rotate(-8deg)"};
    }
  }

  &:disabled {
    background: rgba(23, 23, 28, 0.85);
    color: var(--muted, #8e8c94);
    border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
    box-shadow: none;
    transform: none;
    cursor: not-allowed;

    svg {
      transform: none;
    }
  }
`;

const Label = styled.div`
  font-size: 1.1rem;
  font-family: "Poppins-Medium";
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted, #8e8c94);
  margin-bottom: 0.8rem;
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  flex-shrink: 0;
`;

const QuantityControl = styled.button`
  width: 4.2rem;
  height: 100%;
  min-height: 4.6rem;
  font-size: 1.6rem;
  border: none;
  background: transparent;
  color: var(--muted, #8e8c94);
  cursor: pointer;
  transition: all 0.25s;

  &:hover {
    color: var(--red, #fe5f55);
  }
`;

const QuantityValue = styled.div`
  min-width: 3rem;
  text-align: center;
  font-size: 1.5rem;
  font-family: "Poppins-SemiBold";
  color: var(--text, #f4f2ee);
`;

const SuggestedSection = styled.section`
  width: 100%;
  margin-top: 7rem;
  color: var(--text, #f4f2ee);

  h2 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    font-size: clamp(2.2rem, 3.4vw, 3rem);
    letter-spacing: 0.01em;
    margin-bottom: 0.6rem;
    display: flex;
    align-items: center;
    gap: 1.4rem;

    &::before {
      content: "";
      width: 3.4rem;
      height: 1px;
      background: var(--red, #fe5f55);
    }
  }
`;

const SuggestedList = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  min-width: 94rem;
  align-items: center;
  flex-direction: row;

  @media (max-width: 980px) {
    min-width: 100%;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--bg1, #101013);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--red, #fe5f55);
  border-radius: 0.6rem;
  font-size: 1.2rem;
  font-family: "Poppins-SemiBold";
  z-index: 2;
  pointer-events: none;
`;
